import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
// types
import {
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema,
  assetsCollectionElement,
  IFileAsset,
  IEcommerceVideoAsset,
  ASSETS_TYPES
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'

// functions
import { formatPriceWithLocale, getAssetType } from '@aytm/app/swidget/base/questions/Ecommerce/functions.ts'
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets.ts'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects.ts'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import Rating from './Rating.tsx'
import SubmitModal from './SubmitModal.tsx'
import Carousel from '@aytm/app/swidget/base/questions/Ecommerce/components/Carousel.tsx'
import AddToCartButton from './AddToCartButton.tsx'

// custom hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'

// styles
import style from './Product.module.scss'
import standartStyle from '../Standart.module.scss'
import Subproducts from './Subproducts.tsx'

interface IProductProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  quantity: number
  index: number
  totals: number[]
  findability: boolean
  onAddToCart: (qnt: number, index: number) => void
  onBuyNow: (index: number) => void
  onCartOpen: () => void
  scrollTop: () => void
  checkout: () => void
  onItemSelect: (index: number) => void
  onItemClose: () => void
}

const Product = ({
  answer,
  schema,
  quantity,
  index,
  totals,
  findability,
  onAddToCart,
  onBuyNow,
  onCartOpen,
  scrollTop,
  checkout,
  onItemSelect,
  onItemClose
}: IProductProps) => {
  // custom hooks calls
  const viewport = useViewport()
  const timeline = useTimeline()
  const { t, languageTag } = useSurveyLocale()

  // memoized
  const assetsCollection: assetsCollectionElement[] = useMemo(
    () => [
      ...(answer.image ? [answer.image] : []),
      ...(answer.gallery || []),
      ...(answer.view3d ? [answer.view3d] : [])
    ],
    [answer]
  )

  // state
  const [showModal, setShowModal] = useState<boolean>(false)
  const [onImage, setOnImage] = useState<boolean>(false)
  const [position, setPosition] = useState<null | { x: number; y: number }>(null)
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(() => {
    const defaultGalleryIndex = answer.gallery?.findIndex(img => img.product?.default)
    if (defaultGalleryIndex !== undefined && defaultGalleryIndex !== -1) {
      return defaultGalleryIndex + 1
    }
    if (answer.view3d?.default) {
      return assetsCollection.length - 1
    }
    return 0
  })

  // functions
  const onMouseEnter = () => {
    setOnImage(true)
    timeline.log({
      ref: answer.id,
      type: 'zoom_image_in',
      target: selectedAssetIndex.toString()
    })
  }
  const onMoveOnImage = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onImage) setOnImage(true)
      const element = event.currentTarget
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        const centerX = width / 2
        const centerY = height / 2
        const mouseX = event.clientX - left
        const mouseY = event.clientY - top

        setPosition({
          x: -(mouseX - centerX) * 0.75,
          y: -(mouseY - centerY) * 0.75
        })
      })
    },
    [onImage]
  )

  const resetImageState = () => {
    requestAnimationFrame(() => {
      setOnImage(false)
      setPosition({ x: 0, y: 0 })
    })
    timeline.log({
      ref: answer.id,
      type: 'zoom_image_out',
      target: selectedAssetIndex.toString()
    })
  }

  const navigate = (direction: -1 | 1) => {
    if (-1 === direction) {
      if (0 === selectedAssetIndex) {
        setSelectedAssetIndex(assetsCollection.length - 1)
      } else {
        setSelectedAssetIndex(prevState => prevState - 1)
      }
    } else if (1 === direction) {
      if (selectedAssetIndex === assetsCollection.length - 1) {
        setSelectedAssetIndex(0)
      } else {
        setSelectedAssetIndex(prevState => prevState + 1)
      }
    }
  }

  const onSubitemSelect = (index: number) => {
    onItemClose()
    onItemSelect(index)
    setSelectedAssetIndex(answer.gallery?.findIndex(img => img.product.default) + 1 || 0)
  }

  const playingIndexRef = useRef<number | null>(null)

  const logVideoEvent = (type: 'play_video' | 'pause_video', assetIndex: number) =>
    timeline.log({ ref: answer.id, target: assetIndex.toString(), type })

  // The <video> unmounts on asset switch and product close, so its own pause event never reaches
  // React. Ref-based and idempotent: a delivered pause clears the ref, so nothing can double-log.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  const onVideoPause = () => {
    if (null === playingIndexRef.current) return
    playingIndexRef.current = null
    logVideoEvent('pause_video', selectedAssetIndex)
  }
  const onVideoPlay = () => {
    if (null !== playingIndexRef.current) return
    playingIndexRef.current = selectedAssetIndex
    logVideoEvent('play_video', selectedAssetIndex)
  }

  // effects
  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  useEffect(() => {
    flushPlayingVideo()
    if (!viewport.mobile) {
      timeline.log({
        ref: answer.id,
        type: 'show_asset',
        target: selectedAssetIndex.toString(),
        context: getAssetType(assetsCollection[selectedAssetIndex])?.toLowerCase()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetIndex, viewport.mobile])

  useEffect(
    () => () => flushPlayingVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <section className={`${style.product} ${viewport.mobile ? style.productMobile : ''}`}>
      {showModal && (
        <SubmitModal
          answer={answer}
          schema={schema}
          totals={totals}
          onCartOpen={onCartOpen}
          onModalClose={() => setShowModal(false)}
        />
      )}
      {viewport.mobile ? (
        <div className={style.carouselWrapper}>
          <Carousel schema={answer} store="standart" pagination="default" />
        </div>
      ) : (
        <div className={style.images}>
          {assetsCollection.length > 1 && (
            <div className={style.imagesCarousel}>
              {assetsCollection.map((asset, index) => (
                <button key={index} className={style.itemPreview} onMouseEnter={() => setSelectedAssetIndex(index)}>
                  {index === selectedAssetIndex && <div className={style.activeLayout} />}
                  {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                    <div
                      className={style.imageAsset}
                      role="img"
                      aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                      style={{
                        backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})`
                      }}
                    />
                  )}
                  {ASSETS_TYPES.MODEL === getAssetType(asset) && (
                    <div className={style.fileAsset}>
                      <model-viewer
                        src={(asset as IFileAsset).file_url}
                        camera-controls={false}
                        interaction-prompt="none"
                        style={{ width: '100%', height: '100%' }}
                      />
                      <div className={style.layout} />
                    </div>
                  )}
                  {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
                    <div
                      className={style.videoAsset}
                      role="presentation"
                      style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
                    />
                  )}
                </button>
              ))}
            </div>
          )}
          <div className={style.mainImage}>
            {answer.best_seller && (
              <div className={standartStyle.choice}>
                <div className={standartStyle.choiceIcon} />
                <span className={standartStyle.choiceText}>{t('questions', 'ec', 'ecommerce', 'highlyRated')}</span>
              </div>
            )}
            {assetsCollection.length > 1 && (
              <>
                <button className={style.navPrev} onClick={() => navigate(-1)}>
                  <div className={style.prevIcon} />
                </button>
                <button className={style.navNext} onClick={() => navigate(1)}>
                  <div className={style.nextIcon} />
                </button>
              </>
            )}
            {assetsCollection[selectedAssetIndex] ? (
              <div className={style.bigImage}>
                {ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) && (
                  <div
                    className={style.imageAsset}
                    role="img"
                    aria-label={
                      (assetsCollection[selectedAssetIndex] as IImageAsset).alt_text || t('questions', 'img', 'noAlt')
                    }
                    style={{
                      backgroundImage: `url(${getBigAssetPath(assetsCollection[selectedAssetIndex] as IImageAsset)})`,
                      backgroundPosition: position
                        ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)`
                        : '50% 50%',
                      transform: `scale(${onImage ? 2 : 1})`
                    }}
                    onMouseMove={onMoveOnImage}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={resetImageState}
                  />
                )}
                {ASSETS_TYPES.MODEL === getAssetType(assetsCollection[selectedAssetIndex]) && (
                  <model-viewer
                    src={(assetsCollection[selectedAssetIndex] as IFileAsset).file_url}
                    camera-controls={false}
                    style={{ width: '100%', height: '100%' }}
                  />
                )}
                {ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAssetIndex]) && (
                  <video
                    className={style.videoAsset}
                    src={(assetsCollection[selectedAssetIndex] as IEcommerceVideoAsset).video.url}
                    onPause={onVideoPause}
                    onPlay={onVideoPlay}
                    preload="metadata"
                    muted
                    controls
                    playsInline
                  />
                )}
              </div>
            ) : (
              <div className={style.noImage} />
            )}
          </div>
        </div>
      )}
      <div className={style.description}>
        <div className={style.caption} dangerouslySetInnerHTML={{ __html: answer.text }} />
        {answer.best_seller && viewport.mobile && (
          <div className={style.choice}>
            <div className={style.choiceIcon} />
            <span className={style.choiceText}>{t('questions', 'ec', 'ecommerce', 'highlyRated')}</span>
          </div>
        )}
        <div className={style.prices}>
          {answer.discounted_price && answer.price > answer.discounted_price ? (
            <>
              <div
                className={style.newPrice}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                }}
              />
              <div
                className={style.lastPrice}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                }}
              />
              <div
                className={style.off}
                dangerouslySetInnerHTML={{
                  __html: t<(off: string, applied: boolean) => string>(
                    'questions',
                    'ec',
                    'ecommerce',
                    'off'
                  )(
                    (
                      ((schema.answers[index].price - schema.answers[index].discounted_price) /
                        schema.answers[index].price) *
                      100
                    ).toFixed(1),
                    false
                  )
                }}
              />
              {answer.per_count_price && (
                <div className={style.perCount} dangerouslySetInnerHTML={{ __html: `(${answer.per_count_price})` }} />
              )}
            </>
          ) : (
            <>
              <div
                className={style.defaultPrice}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                }}
              />
              {answer.per_count_price && (
                <div className={style.perCount} dangerouslySetInnerHTML={{ __html: `(${answer.per_count_price})` }} />
              )}
            </>
          )}
        </div>
        <Subproducts answer={answer} schema={schema} onSubitemSelect={onSubitemSelect} />
        {quantity > 0 ? (
          <div className={style.addToCartWrapper}>
            <AddToCartButton
              quantity={quantity}
              findability={findability}
              index={index}
              size={viewport.mobile ? 'small' : 'big'}
              onAddToCart={onAddToCart}
              limit={schema.max_qty_per_product}
            />
          </div>
        ) : (
          <>
            <button
              disabled={
                (findability && quantity > 0) || (schema.max_qty_per_product && quantity >= schema.max_qty_per_product)
              }
              className={`${style.add} ${
                schema.max_qty_per_product && quantity >= schema.max_qty_per_product ? style.disabled : ''
              }`}
              onClick={() => {
                onAddToCart(1, index)
                if (findability) {
                  checkout()
                } else {
                  hapticEffects.selection()
                  setShowModal(true)
                }
              }}
            >
              {t('questions', 'ec', 'ecommerce', 'addToCart')}
            </button>
            <button
              className={style.fav}
              onClick={() => {
                hapticEffects.selection()
                onBuyNow(index)
              }}
            >
              {t('questions', 'ec', 'ecommerce', 'buyNow')}
            </button>
          </>
        )}
        <div className={style.shippingTitle}>{t('questions', 'ec', 'ecommerce', 'shipping')}</div>
        <div className={style.shippingText}>{t('questions', 'ec', 'ecommerce', 'shippingText')}</div>
        <div className={style.pickupTitle}>{t('questions', 'ec', 'ecommerce', 'freePickup')}</div>
        <EmptyLink color="#111">
          <div className={style.findStore}>{t('questions', 'ec', 'ecommerce', 'findStore')}</div>
        </EmptyLink>
        <div
          className={style.answerText}
          dangerouslySetInnerHTML={{
            __html: `${answer.feature_bullets} ${answer.description}`
              .replace(/&lt;ul&gt;/g, '<ul>')
              .replace(/&lt;\/ul&gt;/g, '</ul>')
              .replace(/&lt;li&gt;/g, '<li>')
              .replace(/&lt;\/li&gt;/g, '</li>')
          }}
        />
        <EmptyLink color="#111">
          <div className={style.findStore}>{t('questions', 'ec', 'ecommerce', 'details')}</div>
        </EmptyLink>
        <EmptyLink color="#111">
          <div className={style.additional}>
            <div className={style.text}>{t('questions', 'ec', 'ecommerce', 'shipAndReturn')}</div>
            <div className={style.downIcon} />
          </div>
        </EmptyLink>
        <EmptyLink color="#111">
          <div className={style.additional}>
            <div className={style.text}>{t('questions', 'ec', 'ecommerce', 'qa')}</div>
            <div className={style.downIcon} />
          </div>
        </EmptyLink>
        <EmptyLink color="#111">
          <div className={style.additional}>
            <div
              className={style.text}
              dangerouslySetInnerHTML={{
                __html: `${t('questions', 'ec', 'ecommerce', 'reviews')}(${answer.reviews})`
              }}
            />
            <div className={style.rating}>
              <Rating rating={answer.rating} />
            </div>
            <div className={style.downIcon} />
          </div>
        </EmptyLink>
      </div>
    </section>
  )
}

export default Product
